import { connect } from 'react-redux'
import compose from 'recompose/compose'
import { bindActionCreators } from 'redux'
import { withI18next } from 'lib/i18n/withI18next'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import ProductListSkeleton from 'components/product/product-list-skeleton'
import Typo from 'constants/typography'
import Button from 'components/common/button'
import { BUTTON_TYPES } from 'constants/button'
import Router from 'next/router'
import StateStorage from 'lib/state-storage'
import React, { useRef, useEffect } from 'react'
import useIntersection from 'components/custom-hooks/useIntersection'
import { getProductIDs } from 'lib/utils/handle-response'
import { segmentTrackProductListViewed } from 'lib/segment'
import {
  SEGMENT_STORAGE_KEY_CATEGORY,
  SEGMENT_REF_TYPES,
} from 'lib/segment/const'
import ProductListSlider from 'components/product/product-list-slider'
import homefeedDuck from '../homefeed/duck'
import { getHomefeedSuperDeals } from '../utils'
import styles from './home-page-super-deals.scss'
import homepageProductSliderOptions from '../product-presentation'

const HomepageSuperDeals = ({ isPhone, homefeed }) => {
  const homepageSuperDealsRef = useRef(null)
  const isIntersecting = useIntersection(homepageSuperDealsRef, {
    once: false,
  })
  const shop = StateStorage.getShop()
  const language = StateStorage.getLanguage()
  const superDealsInfo = getHomefeedSuperDeals(homefeed?.payload)
  const feedId =
    superDealsInfo?.superDeals?.relationships?.['feed-items']?.data?.[0]?.id ??
    ''
  const title = superDealsInfo?.superDeals?.attributes?.title
  const titleColor =
    superDealsInfo?.superDeals?.attributes?.title_info?.['text-color']
  const subtitle = superDealsInfo?.superDeals?.attributes?.subtitle
  const showProducts =
    !homefeed?.isFetching && superDealsInfo.superDealsProducts.length > 0
  const cta = superDealsInfo?.superDeals?.attributes?.cta

  const prepareCategoryReferer = (refId = '') => {
    sessionStorage.setItem(SEGMENT_STORAGE_KEY_CATEGORY.id, refId)
    sessionStorage.setItem(
      SEGMENT_STORAGE_KEY_CATEGORY.type,
      SEGMENT_REF_TYPES.APP_FEED,
    )
  }
  const handleClickSeeAll = () => {
    Router.push(
      `/category?id=${cta.value}`,
      `/${shop}/${language}/clothes/${cta.link_rewrite}`,
    )
    prepareCategoryReferer(feedId)
  }

  const id_category = cta?.value ?? ''
  const productsList = superDealsInfo?.superDealsProducts?.map((product) => {
    const productAttributes = product?.attributes
    const productImages = product?.attributes?.images
    const productPrice = productAttributes?.price
    const productStock = productAttributes?.stock
    const productInfo = {
      is_italist: productAttributes?.manufacturer?.is_italist,
      id_product: product?.id,
      id_category_default: product?.relationships?.default_category?.data?.id,
      category_link_rewrite: cta?.link_rewrite,
      manufacturer_name: productAttributes?.manufacturer?.name,
      price: productPrice?.original,
      name: productAttributes?.name,
      is_coming_soon: productAttributes?.is_coming_soon,
      hyphen_name: productAttributes?.link_rewrite,
      is_back_in_stock: productAttributes?.is_back_in_stock,
      is_low_stock: productStock?.is_low_stock,
      is_sold_out: productStock?.is_sold_out,
      stock_quantity: productStock?.quantity || 0,
      price_formatted: productPrice?.original_formatted,
      manufacturer: productAttributes?.manufacturer,
      full_cover_img_v2: productImages?.cover_url ?? '',
      full_flatlay_img_v2: productImages?.flatlay_url ?? '',
      full_hover_img_v2: productImages?.hover_url ?? '',
      discounted_price: productPrice?.discounted,
      reduction_type: productPrice?.reduction?.kind,
      reduction: productPrice?.reduction?.value,
      discount_from: productPrice?.discount_from,
      discount_to: productPrice?.discount_to,
      is_flash_sale: productPrice?.is_flash_sale,
      flash_sale_seconds_left: productPrice?.flash_sale_seconds_left,
      discounted_price_formatted: productPrice?.discounted_formatted,
      is_premium_product: false,
      reduction_formatted: productPrice?.reduction?.value_formatted,
      is_shoes: false,
      is_wishlist: productAttributes?.is_on_wishlist,
    }
    return productInfo
  })

  useEffect(() => {
    if (isIntersecting && showProducts) {
      const productIds = getProductIDs(productsList)
      segmentTrackProductListViewed(
        {
          id_site_category: id_category,
          page_number: 1,
          product_list_ids: productIds,
          site_category_name: cta?.link_rewrite || '',
        },
        {
          ref_type: SEGMENT_REF_TYPES.SPECIAL_AWARENESS,
          ref_id: feedId,
        },
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntersecting, showProducts, feedId])

  // A completed empty feed is not still loading. Do not leave a blank rail
  // or an empty category button above the main product listing.
  if (!homefeed?.isFetching && !showProducts) return null

  return (
    <div
      ref={homepageSuperDealsRef}
      className="home-page-super-deals-container"
    >
      <style jsx>{styles}</style>
      <div className="home-page-super-deals-container__titles-section">
        <div className="home-page-super-deals-container__titles">
          <span
            style={{ ...(titleColor && { color: titleColor }) }}
            className={clsx(
              isPhone ? Typo.subtitle2 : Typo.title3,
              'home-page-super-deals-container__titles__title',
            )}
          >
            {title}
          </span>
          <span className={isPhone ? Typo.caption : Typo.body2}>
            {subtitle}
          </span>
        </div>
        {isPhone && (
          <div className="home-page-super-deals-container__cta">
            <Button
              type={BUTTON_TYPES.outlinedLight}
              onClick={handleClickSeeAll}
            >
              {cta?.title ?? ''}
            </Button>
          </div>
        )}
      </div>
      {showProducts ? (
        <div className={clsx('home-page-super-deals-product-list')}>
          <div className="home-page-super-deals-product-list__product-container">
            <ProductListSlider
              customArrowProps={{
                class: {
                  leftArrow: 'home-page-super-deals-product-list__prev-btn',
                  rightArrow: 'home-page-super-deals-product-list__next-btn',
                },
              }}
              slidesPerView={4}
              customSliderOptions={homepageProductSliderOptions}
              isPhone={isPhone}
              displayBullets={false}
              displayPremiumTags={false}
              displayDescription
              displayWishlistIcon={false}
              displayQuickAddToCart={false}
              displayFlashSaleTitle={false}
              displayManufacturerName={false}
              list={productsList}
              refType={SEGMENT_REF_TYPES.APP_FEED}
              refId={feedId}
            />
          </div>
        </div>
      ) : (
        <ProductListSkeleton number={4} workwearGrid />
      )}
      {!isPhone && (
        <div className="home-page-super-deals-container__cta">
          <Button type={BUTTON_TYPES.outlinedLight} onClick={handleClickSeeAll}>
            {cta?.title ?? ''}
          </Button>
        </div>
      )}
    </div>
  )
}

HomepageSuperDeals.propTypes = {
  isPhone: PropTypes.bool,
  homefeed: PropTypes.shape({
    isFetching: PropTypes.bool,
    payload: PropTypes.shape({}),
  }).isRequired,
}

HomepageSuperDeals.defaultProps = {
  isPhone: false,
}

export default compose(
  connect(
    (state) => ({
      homefeed: state.homefeed,
      isPhone: state.device.isPhone,
    }),
    (dispatch) =>
      bindActionCreators(
        {
          getHomefeed: homefeedDuck.creators.get,
          invalidateHomefeed: homefeedDuck.creators.invalidate,
        },
        dispatch,
      ),
  ),
  withI18next(),
)(HomepageSuperDeals)
